import { useEffect, useState, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { Check, Database, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { UiFragment } from '@/components/landing/service'
import { BRAND, OUT_EASE, halftone, type BrandColor } from '@/components/landing/service/halftone'
import type { PlatformFeature } from '@/data/platform'

/* ----------------------------------------------------------------------------
   Platform-page product fragments drawn in JSX (no photography). Square
   stages share the service kit's look: grey ground, a halftone brand block,
   a white app window. Text scales with the stage via container units.
   Decorative: aria-hidden. Motion runs while `active`, keeps its progress
   when paused, and is complete and static under reduced motion.
---------------------------------------------------------------------------- */

export const fragMuted = 'text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]'
export const fragHair = 'border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-700)]'
const bar = 'block h-[0.55em] bg-[var(--color-landing-gray-200)] dark:bg-[var(--color-landing-gray-700)]'

/** True from the first time `active` is true. */
export function useSeen(active: boolean) {
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    if (active) setSeen(true)
  }, [active])
  return seen
}

/** Types `text` out while `run` is true; keeps its place when paused. */
export function useStream(text: string, run: boolean, step = 2, ms = 26) {
  const reduced = useReducedMotion()
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (reduced || !run || count >= text.length) return
    const id = window.setTimeout(() => setCount((c) => Math.min(text.length, c + step)), ms)
    return () => window.clearTimeout(id)
  }, [reduced, run, count, text.length, step, ms])
  return { shown: reduced ? text : text.slice(0, count), done: reduced || count >= text.length }
}

/** Square stage: grey ground, halftone brand block top-right, app window. */
export function Stage({
  children,
  accent = 'pink',
  corner = 'right',
}: {
  children: ReactNode
  accent?: BrandColor
  corner?: 'left' | 'right'
}) {
  return (
    <div aria-hidden className="relative h-full w-full overflow-hidden bg-[#f3f3f3] [container-type:inline-size] dark:bg-[#15151d]">
      <span
        className={cn('absolute top-0 h-[46%] w-[46%]', corner === 'right' ? 'right-0' : 'left-0')}
        style={{ backgroundColor: BRAND[accent], ...halftone(0.3, 6) }}
      />
      <div
        className={cn(
          'absolute bottom-[9%] top-[11%] flex flex-col overflow-hidden bg-white text-[clamp(9px,2.1cqw,15px)] leading-[1.35] text-[var(--color-landing-gray-900)] shadow-[0_30px_60px_-24px_rgba(10,10,10,0.35)] dark:bg-[#1c1c26] dark:text-white',
          corner === 'right' ? 'left-[7%] right-[11%]' : 'left-[11%] right-[7%]'
        )}
      >
        {children}
      </div>
    </div>
  )
}

export function WindowBar({ app, icon, title, meta }: { app?: OfficeApp; icon?: ReactNode; title: string; meta?: string }) {
  return (
    <div className={cn('flex items-center gap-[0.6em] border-b px-[1em] py-[0.7em]', fragHair)}>
      {app ? <OfficeAppIcon app={app} className="h-[1.9em] w-[1.9em]" /> : icon}
      <span className="truncate font-semibold">{title}</span>
      {meta && <span className={cn('ml-auto shrink-0 text-[0.85em]', fragMuted)}>{meta}</span>}
    </div>
  )
}

export function Tick({ show, delay = 0 }: { show: boolean; delay?: number }) {
  return (
    <motion.span
      className="flex h-[1.4em] w-[1.4em] shrink-0 items-center justify-center text-white"
      style={{ backgroundColor: BRAND.green }}
      initial={false}
      animate={show ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
      transition={{ duration: 0.35, ease: OUT_EASE, delay: show ? delay : 0 }}
    >
      <Check className="h-[0.95em] w-[0.95em]" strokeWidth={3} />
    </motion.span>
  )
}

/* ------------------------------ prompt stage ------------------------------ */

/**
 * A prompt and its streamed answer, the draft's outline filling in as the
 * answer lands. Used by the AI generation page with each use case's demo.
 */
export function PromptStage({
  demo,
  active,
  accent,
  app,
  label,
}: {
  demo: { ask: string; answer: string }
  active: boolean
  accent: BrandColor
  app: OfficeApp
  label: string
}) {
  const { shown, done } = useStream(demo.answer, active)
  const sections = demo.answer.split('.')[0]!.split('·').map((s) => s.trim()).filter(Boolean)
  const progress = demo.answer.length ? shown.length / demo.answer.length : 1
  return (
    <Stage accent={accent} corner={accent === 'pink' ? 'left' : 'right'}>
      <WindowBar app={app} title={label} meta="Templatean AI" />
      <div className="flex flex-1 flex-col gap-[1em] p-[1.2em]">
        <div className="flex items-start gap-[0.7em]">
          <span
            className="flex h-[2em] w-[2em] shrink-0 items-center justify-center text-white"
            style={{ backgroundColor: BRAND[accent], ...halftone(0.25, 4) }}
          >
            <Sparkles className="h-[1.1em] w-[1.1em]" />
          </span>
          <span className={cn('flex-1 border px-[0.9em] py-[0.6em] font-medium', fragHair)}>{demo.ask}</span>
        </div>
        <div className="min-h-[5.5em] bg-[var(--color-landing-gray-50)] p-[0.9em] font-[family-name:var(--font-landing-mono)] text-[0.92em] dark:bg-[#15151d]">
          {shown}
          {!done && <span className="ml-[1px] inline-block h-[1em] w-[0.5em] translate-y-[0.15em]" style={{ backgroundColor: BRAND[accent] }} />}
        </div>
        <div className={cn('flex flex-1 flex-col gap-[0.6em] border p-[0.9em]', fragHair)}>
          {sections.map((section, i) => {
            const on = progress >= (i + 1) / sections.length
            return (
              <div key={section} className="flex items-center gap-[0.7em] transition-opacity duration-300" style={{ opacity: on ? 1 : 0.25 }}>
                <span className="w-[42%] shrink-0 truncate text-[0.9em] font-semibold">{section}</span>
                <span className={cn(bar, 'flex-1')} />
              </div>
            )
          })}
        </div>
        <div className="flex items-center gap-[0.6em]">
          <span className="bg-[var(--color-landing-primary)] px-[1em] py-[0.55em] text-[0.85em] font-bold uppercase text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
            Insert
          </span>
          <span className={cn('text-[0.85em]', fragMuted)}>{done ? 'Ready to insert' : 'Writing…'}</span>
        </div>
      </div>
    </Stage>
  )
}

/* ----------------------- HR fields through a connector -------------------- */

/**
 * Template fields filled from HR data reached through a custom REST or SQL
 * connector (there is no ready-made HR integration).
 */
export function ConnectorFieldsStage({ title, active }: { title: string; active: boolean }) {
  const seen = useSeen(active)
  const fields: Array<[string, string]> = [
    ['Employee', '{{employee.name}}'],
    ['Role', '{{employee.role}}'],
    ['Start date', '{{employee.start_date}}'],
    ['Manager', '{{employee.manager}}'],
  ]
  return (
    <Stage accent="blue">
      <WindowBar app="word" title={title} meta="Variables" />
      <div className="grid flex-1 grid-cols-[1.1fr_1fr]">
        <div className={cn('flex flex-col justify-between gap-[0.85em] border-r p-[1.1em]', fragHair)}>
          <span className="flex items-center gap-[0.5em] bg-[var(--color-landing-gray-50)] px-[0.6em] py-[0.45em] text-[0.82em] font-semibold dark:bg-[#15151d]">
            <Database className="h-[1.1em] w-[1.1em] text-[var(--color-landing-accent)]" />
            REST or SQL connector
          </span>
          {fields.map(([label, token], i) => (
            <div key={label}>
              <span className={cn('block text-[0.8em] font-semibold uppercase tracking-wide', fragMuted)}>{label}</span>
              <motion.span
                className="mt-[0.3em] block border px-[0.6em] py-[0.35em] font-[family-name:var(--font-landing-mono)] text-[0.85em]"
                initial={false}
                animate={
                  seen
                    ? { backgroundColor: 'rgba(12,0,255,0.08)', borderColor: 'rgba(12,0,255,0.5)' }
                    : { backgroundColor: 'rgba(12,0,255,0)', borderColor: 'rgba(160,160,160,0.4)' }
                }
                transition={{ duration: 0.4, ease: OUT_EASE, delay: seen ? 0.3 + i * 0.22 : 0 }}
              >
                {token}
              </motion.span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-[0.7em] p-[1.1em]">
          <span className={cn(bar, 'w-2/3 bg-[var(--color-landing-gray-900)] dark:bg-white')} />
          {fields.map(([label], i) => (
            <span key={label} className="flex items-center gap-[0.4em]">
              <span className={cn(bar, 'w-1/3')} />
              <motion.span
                className="block h-[0.7em] w-1/2 origin-left bg-[var(--color-landing-accent)]"
                initial={false}
                animate={{ scaleX: seen ? 1 : 0 }}
                transition={{ duration: 0.45, ease: OUT_EASE, delay: seen ? 0.45 + i * 0.22 : 0 }}
              />
            </span>
          ))}
          {[90, 100, 72, 84, 100, 66].map((w, i) => (
            <span key={i} className={bar} style={{ width: `${w}%` }} />
          ))}
        </div>
      </div>
    </Stage>
  )
}

/**
 * Use-case visual from the service kit, except HR use cases, which draw the
 * connector version (the shared fragment labels its source "HRIS").
 */
export function UseCaseVisual({
  useCase,
  active,
}: {
  useCase: PlatformFeature['useCases'][number]
  active: boolean
}) {
  if (/human resources/i.test(useCase.industry)) {
    return <ConnectorFieldsStage title={useCase.title} active={active} />
  }
  return (
    <UiFragment kind={useCase.fragment} title={useCase.title} industry={useCase.industry} demo={useCase.demo} active={active} />
  )
}
